import {
  ChevronDown,
  ChevronUp,
  Globe,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { useState } from 'react';

import { RainmakerLogo } from '@/components/rainmaker/RainmakerLogo';
import { Button } from '@/components/ui/button';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import type { ProviderEnrichmentData } from '@/services/types/treatment-types';
import { sortHandlesByPreferred } from '@/utils/enrichment-adapter';

import { EnrichmentConfidenceBadge } from './EnrichmentConfidenceBadge';
import { EnrichmentHandleCard } from './EnrichmentHandleCard';
import { EnrichmentSourcesIndicator } from './EnrichmentSourcesIndicator';

interface EnrichmentSummaryProps {
  /** The enrichment data to display */
  enrichment: ProviderEnrichmentData;
  /** Provider name for display */
  providerName?: string | null;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Hide contact handles entirely */
  hideHandles?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Full enrichment data display with records request notes and handles.
 * Used in the provider detail page and pending approval cards.
 */
export const EnrichmentSummary = ({
  enrichment,
  providerName,
  compact = false,
  hideHandles = false,
  className,
}: EnrichmentSummaryProps) => {
  const [showAllHandles, setShowAllHandles] = useState(false);

  const sortedHandles = sortHandlesByPreferred(
    enrichment.handles.filter((h) => h.value),
  );
  const preferredHandles = sortedHandles.filter((h) => h.preferred);
  const otherHandles = sortedHandles.filter((h) => !h.preferred);

  // In compact mode, show preferred handles + first other handle
  const displayedHandles = compact
    ? showAllHandles
      ? sortedHandles
      : [...preferredHandles, ...otherHandles.slice(0, 1)]
    : sortedHandles;
  const hasMoreHandles = compact && !showAllHandles && otherHandles.length > 1;

  // Collect all unique sources from handles for display
  const allSources = enrichment.handles
    .flatMap((h) => h.sources || [])
    .filter(
      (source, index, self) =>
        index === self.findIndex((s) => s.url === source.url),
    );

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with confidence badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-zinc-600">
          <Globe className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium">Web Research Results</span>
        </div>
        <EnrichmentConfidenceBadge
          confidence={enrichment.confidence}
          size={compact ? 'sm' : 'md'}
        />
      </div>

      {/* Confirmed organization name (if different from provider name) */}
      {enrichment.confirmed_organization_name &&
        enrichment.confirmed_organization_name !== providerName && (
          <div className="text-sm text-zinc-600">
            <span className="font-medium text-zinc-700">Verified as: </span>
            {enrichment.confirmed_organization_name}
          </div>
        )}

      {/* Records request method notes */}
      {enrichment.records_request_method_notes && (
        <div
          className={cn(
            'rounded-lg border p-3',
            compact
              ? 'border-zinc-200 bg-zinc-50'
              : 'border-violet-100 bg-violet-50/30',
          )}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              How to Request Records
            </span>
            <span className="inline-flex items-center gap-1 text-[9px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">
              <RainmakerLogo size={10} className="text-violet-500" />
              Rainmaker
            </span>
          </div>
          <p className="text-xs text-zinc-800">
            {enrichment.records_request_method_notes}
          </p>
        </div>
      )}

      {/* Contact handles */}
      {!hideHandles && displayedHandles.length > 0 && (
        <div className="space-y-2">
          {!compact && (
            <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              Discovered Contacts
            </div>
          )}
          {displayedHandles.map((handle, index) => (
            <EnrichmentHandleCard
              key={handle.id || index}
              handle={handle}
              compact={compact}
              showSources={!compact}
            />
          ))}

          {/* Show more button in compact mode */}
          {compact && (hasMoreHandles || showAllHandles) && (
            <button
              type="button"
              onClick={() => setShowAllHandles(!showAllHandles)}
              className="flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900 font-medium"
            >
              {showAllHandles ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  {otherHandles.length - 1} more contact
                  {otherHandles.length - 1 !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* All sources summary in compact mode */}
      {compact && allSources.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Sources:</span>
          <EnrichmentSourcesIndicator sources={allSources} compact />
        </div>
      )}
    </div>
  );
};

interface EnrichmentRecordsGuidanceProps {
  /** The enrichment data */
  enrichment: ProviderEnrichmentData;
  /** Provider ID for feedback tracking */
  providerId?: string;
  /** Additional class names */
  className?: string;
}

/**
 * Section for records request guidance.
 * Used in provider cards where space is limited.
 */
export const EnrichmentRecordsGuidance = ({
  enrichment,
  providerId,
  className,
}: EnrichmentRecordsGuidanceProps) => {
  const [feedback, setFeedback] = useState<'helpful' | 'not_helpful' | null>(
    null,
  );
  const capture = usePostHogCapture();

  if (!enrichment.records_request_method_notes) {
    return null;
  }

  // Collect all sources for attribution
  const allSources = enrichment.handles
    .flatMap((h) => h.sources || [])
    .filter(
      (source, index, self) =>
        index === self.findIndex((s) => s.url === source.url),
    )
    .slice(0, 3); // Limit to 3 sources

  const handleFeedback = (helpful: boolean) => {
    const newFeedback = helpful ? 'helpful' : 'not_helpful';
    setFeedback(newFeedback);
    capture('enrichment_records_guidance_feedback', {
      page: 'treatment',
      component: 'records_guidance',
      helpful,
      provider_id: providerId,
      confidence: enrichment.confidence,
    });
  };

  return (
    <div className={cn('border-t border-zinc-100 pt-2 mt-2', className)}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs text-zinc-600 font-medium">
          How to Request Records
        </span>
        <span className="inline-flex items-center gap-1 text-[9px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">
          <RainmakerLogo size={10} className="text-violet-500" />
          Rainmaker
        </span>
      </div>
      <p className="text-xs text-zinc-700">
        {enrichment.records_request_method_notes}
      </p>
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex items-center gap-2">
          <EnrichmentConfidenceBadge
            confidence={enrichment.confidence}
            size="sm"
          />
          {allSources.length > 0 && (
            <EnrichmentSourcesIndicator sources={allSources} compact />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6',
              feedback === 'helpful'
                ? 'text-green-600 bg-green-50'
                : 'text-zinc-400 hover:text-green-600',
            )}
            onClick={() => handleFeedback(true)}
            disabled={feedback !== null}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6',
              feedback === 'not_helpful'
                ? 'text-red-600 bg-red-50'
                : 'text-zinc-400 hover:text-red-600',
            )}
            onClick={() => handleFeedback(false)}
            disabled={feedback !== null}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
